/**
 * L'elenco delle commesse: la prima schermata della giornata.
 *
 * **In contorno Carta**, perche' qui si legge un elenco e non si giudica
 * un'immagine: il fondo chiaro e' quello su cui una tabella si legge meglio, e
 * il contorno lo decide lo schermo e non la persona (§ 01).
 *
 * **Si sceglie con una riga, non con un UUID.** Prima di questa schermata la
 * commessa arrivava dall'indirizzo: qualcuno la copiava da una console. Il
 * primo gesto della giornata non puo' essere incollare un identificatore.
 *
 * **La riga dice il lavoro.** Quanti candidati restano, quanti sono decisi, a
 * che punto sta l'acquisizione: sono le cose che scelgono la commessa. Il
 * paese e il tipo d'impianto non l'hanno mai scelta, e stanno in fondo.
 */
import { useEffect, useMemo, useState } from 'react'

import { ServiceError } from './api'
import { EmptyState } from './carta/EmptyState'
import { StatusPill, type PillTone } from './carta/StatusPill'
import { PoolMap } from './carta/PoolMap'
import { Levels } from './design/indicators'
import { rankOf } from './design/scales'
import { ago, day, decimal, moment } from './format'
import { useT } from './i18n/LocaleProvider'
import type { MessageKey } from './i18n/messages'
import { commesse } from './inspections'
import type { Commessa } from './types'

/** Quali commesse: tutte, quelle con lavoro da giudicare, quelle congelate
 *  per il controllo di qualita' (GI-279). */
export type InspectionListStatus = 'all' | 'work' | 'frozen'
export type InspectionListSort = 'priority' | 'recent' | 'activity'

export interface InspectionListState {
  status: InspectionListStatus
  sort: InspectionListSort
  page: number
}

// Due righe costringevano a paginare anche turni con molte commesse aperte:
// la pagina diventava un controllo di numeri invece che una scelta di lavoro.
//
// Esportata perche' una prova che vuole «piu' di una pagina» deve contarne le
// righe da qui: scrivendo il numero a mano, il giorno in cui la pagina cresce
// la prova continua a passare senza piu' provare la paginazione.
export const PAGE_SIZE = 10

type Translate = (key: MessageKey, vars?: Record<string, string | number>) => string

/** Come si chiama, per chi legge, lo stato in cui sta il pacchetto. */
export function stato(one: Commessa, t: Translate): { label: string; tone: string } {
  if (one.acquisition === 'failed') return { label: t('pool.statusError'), tone: 'sev-4' }
  if (one.acquisition === 'running') return { label: t('pool.statusProcessing'), tone: 'ref' }
  if (one.acquisition === 'received') return { label: t('pool.statusQueued'), tone: 'ink-3' }
  // Il lavoro che resta viene prima della consegna gia' partita: una commessa
  // consegnata a cui e' arrivato un candidato nuovo e' una commessa da
  // verificare, e scriverci «Consegnato» nasconderebbe proprio quel lavoro.
  // Visto guardando l'elenco: quattro candidati in attesa sotto la pillola
  // viola della consegna.
  if (one.waiting > 0) return { label: t('pool.statusToReview'), tone: 'sev-2' }
  if (one.delivery === 'sent' || one.delivery === 'submitted' || one.delivery === 'accepted')
    return { label: t('pool.statusDelivered'), tone: 'st-lock' }
  return { label: t('pool.statusReady'), tone: 'sev-0' }
}

/** Se la commessa sta nel filtro scelto. «Con lavoro» comprende anche cio' che
 *  e' ancora in acquisizione: e' lavoro che arriva. */
function matches(one: Commessa, status: InspectionListStatus): boolean {
  if (status === 'all') return true
  if (status === 'frozen') return one.frozen === true
  if (one.acquisition === 'running' || one.acquisition === 'received' || one.acquisition === 'failed') return true
  return one.waiting > 0
}

function sortedRows(rows: Commessa[], sort: InspectionListSort): Commessa[] {
  const cloned = [...rows]
  if (sort === 'recent') {
    cloned.sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
    return cloned
  }
  if (sort === 'activity') {
    // Chi si e' mosso per ultimo in cima; chi non si e' mai mosso in fondo,
    // fra loro per lavoro che resta.
    cloned.sort((a, b) => {
      const ta = a.last_activity_at == null ? -1 : Date.parse(a.last_activity_at)
      const tb = b.last_activity_at == null ? -1 : Date.parse(b.last_activity_at)
      if (tb !== ta) return tb - ta
      return b.waiting - a.waiting
    })
    return cloned
  }
  cloned.sort((a, b) => {
    if (b.waiting !== a.waiting) return b.waiting - a.waiting
    return Date.parse(b.created_at) - Date.parse(a.created_at)
  })
  return cloned
}

const STATUSES: InspectionListStatus[] = ['all', 'work', 'frozen']

function PoolControls({
  state,
  onStateChange,
  pages = 1,
}: {
  state: InspectionListState
  onStateChange: (next: InspectionListState) => void
  /** Quante pagine ci sono; una sola nasconde le frecce. */
  pages?: number
}): JSX.Element {
  const t = useT()
  const name = (one: InspectionListStatus): string =>
    one === 'all' ? t('pool.filterReady') : one === 'work' ? t('pool.filterInReview') : t('pool.filterFrozen')
  return (
    <section className="pool-controlli" aria-label={t('pool.routeState')}>
      {/* Tre segmenti, non un menu a due voci (GI-279): si vede che cosa si
          sta guardando e che cos'altro c'e', senza aprire niente. */}
      <div className="pool-segmenti" role="group" aria-label={t('pool.filterStatus')}>
        {STATUSES.map((one) => (
          <button
            key={one}
            type="button"
            className={one === state.status ? 'pool-segmento on' : 'pool-segmento'}
            aria-pressed={one === state.status}
            onClick={() => onStateChange({ ...state, status: one, page: 1 })}
          >
            {name(one)}
          </button>
        ))}
      </div>
      <label className="pool-ordine">
        <span className="tenue">{t('pool.sort')}</span>{' '}
        <select
          aria-label={t('pool.sort')}
          value={state.sort}
          onChange={(event) =>
            onStateChange({
              ...state,
              sort:
                event.target.value === 'recent'
                  ? 'recent'
                  : event.target.value === 'activity'
                    ? 'activity'
                    : 'priority',
              page: 1,
            })
          }
        >
          <option value="priority">{t('pool.sortPriority')}</option>
          <option value="recent">{t('pool.sortRecent')}</option>
          <option value="activity">{t('pool.sortActivity')}</option>
        </select>
      </label>
      {pages > 1 && (
        <nav className="pool-pagine" aria-label={t('pool.page')}>
          <button
            type="button"
            aria-label={t('pool.prevPage')}
            disabled={state.page <= 1}
            onClick={() => onStateChange({ ...state, page: Math.max(1, state.page - 1) })}
          >
            ‹
          </button>
          <span className="t-num tenue">{t('pool.pageOf', { n: state.page, m: pages })}</span>
          <button
            type="button"
            aria-label={t('pool.nextPage')}
            disabled={state.page >= pages}
            onClick={() => onStateChange({ ...state, page: Math.min(pages, state.page + 1) })}
          >
            ›
          </button>
        </nav>
      )}
    </section>
  )
}

export function InspectionList({
  onOpen,
  onOpenJob,
  state,
  onStateChange,
  notice,
}: {
  onOpen: (id: string) => void
  /** Entra nella panoramica della commessa, senza aprire una sessione. */
  onOpenJob: (id: string) => void
  state: InspectionListState
  onStateChange: (next: InspectionListState) => void
  // Cio' che il pool condiviso ha da dire sulla sessione di verifica. Sta
  // dentro questo `main` e non accanto: una pagina con due punti di riferimento
  // costringe chi legge con un lettore di schermo a cercare in due posti.
  notice?: JSX.Element | null
}): JSX.Element {
  const t = useT()
  const [rows, setRows] = useState<Commessa[] | null>(null)
  const [chosen, setChosen] = useState<string | null>(null)
  const [failure, setFailure] = useState<ServiceError | null>(null)

  useEffect(() => {
    commesse()
      .then(setRows)
      .catch((error: unknown) => {
        if (error instanceof ServiceError) setFailure(error)
      })
  }, [])

  const matching = useMemo(() => {
    if (!rows) return []
    return sortedRows(
      rows.filter((one) => matches(one, state.status)),
      state.sort,
    )
  }, [rows, state.sort, state.status])
  const pages = Math.max(1, Math.ceil(matching.length / PAGE_SIZE))

  const visible = useMemo(() => {
    const start = (state.page - 1) * PAGE_SIZE
    return matching.slice(start, start + PAGE_SIZE)
  }, [matching, state.page])

  if (failure) {
    return (
      <main>
        <h2>{t('pool.readFailed')}</h2>
        <p role="alert">{failure.message}</p>
        {failure.requestId && (
          <p>
            <small>{t('pool.requestId', { id: failure.requestId })}</small>
          </p>
        )}
      </main>
    )
  }

  if (!rows) {
    return (
      <main>
        {notice}
        <PoolControls state={state} onStateChange={onStateChange} />
        <p role="status">{t('pool.loading')}</p>
      </main>
    )
  }

  if (rows.length === 0) {
    // Non un elenco vuoto e basta: chi guarda deve sapere se e' il sistema a
    // non avere niente o e' lui a guardare nel posto sbagliato.
    return (
      <main>
        {notice}
        <PoolControls state={state} onStateChange={onStateChange} />
        <EmptyState title={t('pool.emptyTitle')} action={t('pool.emptyAction')} />
      </main>
    )
  }

  if (matching.length === 0) {
    return (
      <main className="commesse">
        {notice}
        <PoolControls state={state} onStateChange={onStateChange} />
        {/* Il vuoto si dice per quello che e': «nessuna commessa ha candidati
            adesso» non e' un guasto, e il tasto rimette davanti tutte le
            commesse. */}
        <EmptyState
          title={state.status === 'frozen' ? t('pool.frozenEmptyTitle') : t('pool.filterEmptyTitle')}
          action={state.status === 'frozen' ? t('pool.frozenEmptyAction') : t('pool.filterEmptyAction')}
        >
          <button
            type="button"
            className="primario"
            onClick={() => onStateChange({ ...state, status: 'all', page: 1 })}
          >
            {t('pool.showInReview')}
          </button>
        </EmptyState>
      </main>
    )
  }

  return (
    <main className="commesse">
      {notice}
      <PoolControls state={state} onStateChange={onStateChange} pages={pages} />
      {/*
        Le quattro domande del turno prima della tabella (S17, L-4): quanto
        aspetta e di che gravita', chi ci sta lavorando adesso, che cosa e'
        fermo, che cosa e' in consegna. Poi la tabella e, accanto, la rete: le
        stesse commesse come linee, col tono dello stato.
      */}
      <Tiles rows={rows} />
      <div className="commesse-body">
      <div className="tabella-larga">
        <table>
          <thead>
            <tr>
              <th>{t('pool.colJob')}</th>
              <th>{t('pool.colStatus')}</th>
              <th className="numero">{t('pool.colWaiting')}</th>
              {/* Il lavoro gia' in mano a qualcuno. Senza, due operatori aprono
                  la stessa commessa e il secondo trova solo righe prese. */}
              <th className="numero">{t('pool.colLeased')}</th>
              {/* Chi c'e' sopra e da quanto e' ferma: le due cose che un
                  supervisore guarda per distribuire un turno (GI-250). */}
              <th>{t('pool.colOperators')}</th>
              <th>{t('pool.colActivity')}</th>
              <th className="numero">{t('pool.colDecided')}</th>
              <th>{t('pool.colAcquired')}</th>
              <th>{t('pool.colPlant')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visible.map((one) => {
              const which = stato(one, t)
              return (
                <tr
                  key={one.inspection_id}
                  className={one.inspection_id === chosen ? 'on' : undefined}
                  aria-selected={one.inspection_id === chosen}
                  onClick={() => setChosen(one.inspection_id)}
                >
                  <td className="t-num">{one.external_inspection_id}</td>
                  <td>
                    <StatusPill
                      label={one.phase ? `${which.label} · ${one.phase}` : which.label}
                      tone={which.tone as PillTone}
                      pulse={which.tone === 'ref'}
                    />
                  </td>
                  <td className="numero t-num">
                    {decimal(one.waiting)}
                    {/* Accanto alla cifra, di che gravita' e': dodici di livello
                        3 e dodici di cui tre di livello 1 sono due mattine
                        diverse, e la cifra sola non lo dice (GI-224). Il punto
                        separa la cifra dai gradi: senza, «2 ▲▲S4 2» si leggeva
                        come una sola cosa. */}
                    {one.waiting > 0 && Object.keys(one.waiting_by_level ?? {}).length > 0 && (
                      <span className="tenue"> · </span>
                    )}
                    <Levels counts={one.waiting_by_level} />
                  </td>
                  <td className="numero t-num">{decimal(one.leased ?? 0)}</td>
                  <td className="pool-chi">
                    {one.working !== undefined && one.working.length > 0 ? (
                      one.working.join(', ')
                    ) : (
                      <span className="tenue">{t('pool.nobody')}</span>
                    )}
                  </td>
                  <td className="t-num">
                    {one.last_activity_at == null ? (
                      <span className="tenue">{t('pool.never')}</span>
                    ) : (
                      <time dateTime={one.last_activity_at} title={moment(one.last_activity_at)}>
                        {ago(one.last_activity_at)}
                      </time>
                    )}
                  </td>
                  <td className="numero t-num">{decimal(one.decided)}</td>
                  <td className="t-num">{day(one.created_at)}</td>
                  <td>
                    {one.plant_type} · {one.country}
                  </td>
                  <td>
                    <button
                      type="button"
                      className={one.waiting > 0 ? '' : 'primario'}
                      onClick={() => onOpenJob(one.inspection_id)}
                    >
                      {t('pool.openJob')}
                    </button>
                    {/* La coda si offre solo se si apre davvero: su una
                        commessa congelata il bottone porterebbe a un rifiuto,
                        e «da verificare 2» accanto a una coda chiusa e' una
                        promessa non mantenuta (GI-204). Al posto del bottone,
                        la parola che spiega dove sta il lavoro. */}
                    {one.waiting > 0 && one.frozen !== true && (
                      <button
                        type="button"
                        className="primario"
                        onClick={() => onOpen(one.inspection_id)}
                      >
                        {t('pool.openQueue')}
                      </button>
                    )}
                    {one.waiting > 0 && one.frozen === true && (
                      <span className="tenue">{t('pool.inQualityControl')}</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <section className="commesse-map" aria-label={t('pool.mapArea')}>
        <h2>{t('pool.mapArea')}</h2>
        <PoolMap
          rows={rows}
          tones={Object.fromEntries(rows.map((one) => [one.inspection_id, stato(one, t).tone]))}
          chosen={chosen}
          onPick={setChosen}
        />
        <p className="tenue commesse-map-nota">{t('pool.mapHint')}</p>
      </section>
      </div>
    </main>
  )
}

/** Le quattro domande del turno, in cifre (S17, L-4). */
function Tiles({ rows }: { rows: Commessa[] }): JSX.Element {
  const t = useT()
  const waitingJobs = rows.filter((one) => one.waiting > 0)
  const waiting = rows.reduce((sum, one) => sum + one.waiting, 0)
  const worst = rows.reduce<number | null>((best, one) => {
    for (const level of [1, 2, 3]) if ((one.waiting_by_level?.[String(level)] ?? 0) > 0) return best === null ? level : Math.min(best, level)
    return best
  }, null)
  const leased = rows.reduce((sum, one) => sum + (one.leased ?? 0), 0)
  const people = new Set(rows.flatMap((one) => one.working ?? []))
  const staleAfter = Date.now() - 3 * 86400 * 1000
  const stale = rows.filter(
    (one) => one.waiting > 0 && (one.last_activity_at == null || Date.parse(one.last_activity_at) < staleAfter),
  )
  const frozen = rows.filter((one) => one.frozen === true).length
  const delivering = rows.filter((one) => one.delivery !== null && one.delivery !== undefined).length
  return (
    <dl className="commesse-tiles">
      <div>
        <dt>{t('pool.kpiReview')}</dt>
        <dd className="t-num">
          {t('pool.kpiReviewLine', { jobs: decimal(waitingJobs.length), n: decimal(waiting) })}
          {worst !== null && <span className="tenue"> · {t('pool.kpiWorst', { rank: rankOf(worst).name })}</span>}
        </dd>
      </div>
      <div>
        <dt>{t('pool.kpiProgress')}</dt>
        <dd className="t-num">{t('pool.kpiProgressLine', { n: decimal(leased), people: decimal(people.size) })}</dd>
      </div>
      <div>
        <dt>{t('pool.kpiStale')}</dt>
        <dd className="t-num">{t('pool.kpiStaleLine', { n: decimal(stale.length) })}</dd>
      </div>
      <div>
        <dt>{t('pool.kpiDelivery')}</dt>
        <dd className="t-num">{t('pool.kpiDeliveryLine', { frozen: decimal(frozen), sent: decimal(delivering) })}</dd>
      </div>
    </dl>
  )
}
